import {
  Button,
  Div,
  ErrorToast,
  H5,
  Icon,
  Img,
  MinusIcon,
  Modal,
  SuccessToast,
  PropsModal,
  previewFrontSize,
  previewBackSize,
  updateFrontReceptSize,
  updateBackReceptSize,
  reportError,
} from '@vgrubs/components';
import { PreviewReceiptSizeResponse } from 'components/src/types/api/location.types';
import { Dispatch, SetStateAction } from 'react';

export const SlipFontSizeModal = ({
  modalIsOpen,
  setModalIsOpen,
  closeModal,
  setLoading,
  id,
}: PropsModal & {
  modalIsOpen: {
    data: PreviewReceiptSizeResponse;
    type: 'front' | 'back';
  } | null;
  setModalIsOpen: Dispatch<
    SetStateAction<{
      data: PreviewReceiptSizeResponse;
      type: 'front' | 'back';
    } | null>
  >;
  id: number;
}) => {
  const slip = modalIsOpen;

  async function handlePreviewFontSize(value: number) {
    if (value < 3)
      return ErrorToast(
        'The minimum allowed slip size has been reached. You cannot decrease it further.'
      );

    if (value > 20)
      return ErrorToast(
        'The maximum allowed slip size has been reached. You cannot increase it further.'
      );
    if (!slip?.type) return;
    try {
      setLoading?.(true);
      const { data } =
        slip.type === 'front'
          ? await previewFrontSize(id, { value })
          : await previewBackSize(id, { value });

      setModalIsOpen((prev) => {
        if (!prev) return null;
        return { ...prev, data: data.data };
      });
    } catch (error) {
      reportError(error);
    }
  }

  async function handleUpdateFontSize() {
    if (!slip?.type) return;
    if (!slip?.data?.size) return;
    try {
      setLoading?.(true);
      const { data } =
        slip?.type === 'front'
          ? await updateFrontReceptSize(id, { value: slip.data.size })
          : await updateBackReceptSize(id, { value: slip.data.size });
      SuccessToast(data.message);
      setModalIsOpen(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  const handleImageLoad = () => {
    setLoading?.(false); // stop loading once image is loaded
  };

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`Manage ${slip?.type === 'front' ? 'Front' : 'Kitchen'} Slip`}
      zIndex={111}
    >
      <Div $flex $alignCenter $between $mb="24">
        <H5 $m="0">Change Size</H5>
        <Div $flex $alignCenter $gap="16px">
          <Icon
            $pointer
            onClick={() => handlePreviewFontSize((slip?.data?.size ?? 0) + 1)}
          >
            <PlusIcon />
          </Icon>
          <Icon
            $pointer
            onClick={() => handlePreviewFontSize((slip?.data?.size ?? 0) - 1)}
          >
            <MinusIcon />
          </Icon>
        </Div>
      </Div>

      <Img
        alt=""
        src={slip?.data?.url}
        $width="100%"
        $borderRadius="16px"
        onLoad={handleImageLoad}
      />

      <Button $selected $w="100%" $mt="16" onClick={handleUpdateFontSize}>
        Save
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={() => closeModal?.()}>
        Cancel
      </Button>
    </Modal>
  );
};

export const PlusIcon = () => (
  <svg
    width="33"
    height="33"
    viewBox="0 0 33 33"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_23397_18530)">
      <path
        d="M16.7891 32.2832C21.0325 32.2832 25.1022 30.5975 28.1028 27.5969C31.1034 24.5963 32.7891 20.5267 32.7891 16.2832C32.7891 12.0397 31.1034 7.97008 28.1028 4.96949C25.1022 1.96891 21.0325 0.283203 16.7891 0.283203C12.5456 0.283203 8.47594 1.96891 5.47535 4.96949C2.47477 7.97008 0.789063 12.0397 0.789062 16.2832C0.789063 20.5267 2.47477 24.5963 5.47535 27.5969C8.47594 30.5975 12.5456 32.2832 16.7891 32.2832ZM15.2891 21.7832V17.7832H11.2891C10.4578 17.7832 9.78906 17.1145 9.78906 16.2832C9.78906 15.452 10.4578 14.7832 11.2891 14.7832H15.2891V10.7832C15.2891 9.95195 15.9578 9.2832 16.7891 9.2832C17.6203 9.2832 18.2891 9.95195 18.2891 10.7832V14.7832H22.2891C23.1203 14.7832 23.7891 15.452 23.7891 16.2832C23.7891 17.1145 23.1203 17.7832 22.2891 17.7832H18.2891V21.7832C18.2891 22.6145 17.6203 23.2832 16.7891 23.2832C15.9578 23.2832 15.2891 22.6145 15.2891 21.7832Z"
        fill="#E61E29"
      />
    </g>
    <defs>
      <clipPath id="clip0_23397_18530">
        <rect
          width="32"
          height="32"
          fill="white"
          transform="translate(0.789062 0.283203)"
        />
      </clipPath>
    </defs>
  </svg>
);
